import { useTranslation } from "react-i18next";
import { useRestoreRevision, useRevisionParts } from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";

/**
 * W9 §A.2.3 M11/M12 — "Contributors by part" under a selected revision, with a per-part
 * restore. Skipped for the synthetic `"latest"` id, the same rule the wire enforces.
 */
export function RevisionParts({
  documentId,
  revisionId,
  canRestore,
}: {
  documentId: string;
  revisionId: string | undefined;
  canRestore: boolean;
}) {
  const { t } = useTranslation();
  const { data: parts, isLoading } = useRevisionParts(revisionId ?? null);
  const restore = useRestoreRevision(documentId);

  if (!revisionId || revisionId === "latest") {
    return null;
  }
  if (isLoading || !parts || parts.length === 0) {
    return null;
  }

  // §E-14 — the v2 engine has no `partId`-assigning plugin yet (a W6 dependency this wave does
  // not widen scope to fix). When every part id is the server's positional fallback
  // (`seq:<sequence>:<hash>`), part identity is not stable — say so, rather than pretend a
  // position-derived key means the same thing a real one does.
  const degraded = parts.every((part) => part.partId.startsWith("seq:"));

  return (
    <div className="mt-4 flex flex-col gap-2 border-t border-divider pt-4">
      <Text as="h3" variant="small" weight="medium">
        {t("Contributors by part")}
      </Text>
      {degraded ? (
        <Text variant="small">
          {t("This document has no stable part identity yet — merging will compare by position")}
        </Text>
      ) : null}
      <ul className="flex flex-col gap-1.5">
        {parts.map((part) => (
          <li key={part.id} className="flex items-center gap-2 text-small">
            <span className="text-text-tertiary">{part.partType}</span>
            <span className="min-w-0 flex-1 truncate text-text-secondary">
              {part.author.kind === "ai"
                ? t("Written by {{model}}", { model: part.author.aiModel ?? "AI" })
                : t("Written by a person")}
            </span>
            {canRestore ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={restore.isPending}
                onClick={() =>
                  restore.mutate(
                    { revisionId, partId: part.partId },
                    { onSuccess: () => undefined },
                  )
                }
              >
                {t("Restore this part")}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {canRestore ? (
        <div>
          <Button
            size="sm"
            variant="neutral"
            disabled={restore.isPending}
            onClick={() => restore.mutate({ revisionId })}
          >
            {t("Restore this whole revision")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
